export interface Banner {
  selected_scenario: string;
  pm_target: number;
  current_verified_gdp: number | null;
  current_observation_period: string | null;
  current_source_agency: string | null;
  current_release_date: string | null;
  current_gap_to_target_pp: number | null;
  selected_simulated_gdp: number;
  selected_gap_to_target_pp: number;
}

export interface AreaContribution {
  policy_area_id: string;
  policy_area_name: string;
  color: string;
  contribution_pp: number;
  linkage_count: number;
}

export interface LinkageContribution {
  link_id: string;
  variable_id: string;
  variable_name: string;
  policy_area: string;
  method: string;
  contribution_pp: number;
  baseline?: number | null;
  scenario_value?: number | null;
  shock?: number | null;
  coefficient?: number | null;
  qual_max_pp?: number | null;
  trace?: { label: string; value: unknown; unit?: string; note?: string }[];
}

export interface ScenarioResult {
  scenario_id: string;
  scenario_name: string;
  baseline_growth: number;
  direct_contribution_pp: number;
  interaction_effects_pp: number;
  simulated_gdp_growth: number;
  pm_target: number;
  gap_to_target_pp: number;
  area_contributions: AreaContribution[];
  linkage_contributions: LinkageContribution[];
  interaction_effects: { name: string; interaction_type: string; max_effect_pp: number; intensity: number; contribution_pp: number }[];
  five_year_path: number[];
  unconstrained_gdp_growth?: number | null;
  binding_constraints?: string[];
  advisories?: string[];
  bop_ceiling?: number | null;
  constraint_mode?: string;
  target_gdp_growth?: number | null;
  bottom_up_gdp_growth?: number | null;
  calibration_factor?: number | null;
  calibration_mode?: string;
}

export interface Dashboard {
  custom_defined?: boolean;
  banner: Banner;
  selected: ScenarioResult;
  all_scenarios: ScenarioResult[];
  top_contributors: LinkageContribution[];
  largest_gaps: { variable_id: string; variable_name: string; policy_area_id?: string; scenario_position: string }[];
  area_summary: { policy_area: string; policy_area_id?: string; color: string; contribution_pp: number; attainment_label: string }[];
}

export interface Variable {
  id: string;
  name: string;
  policy_area_id: string;
  policy_area_name: string;
  hierarchy_group: string | null;
  var_type: string | null;
  unit: string | null;
  model_role: string | null;
  direction: number | null;
  gdp_link_active: boolean;
  assumptions: Record<string, number | null>;
  pm_target: number | null;
  weight: number | null;
  phase_in: number | null;
  qualitative_max_pp: number | null;
  data_status: string | null;
  source_org: string | null;
}

export interface VettingItem {
  variable_id: string;
  variable_name: string;
  policy_area_id: string;
  policy_area_name: string;
  direction: number;
  actual_value: number | null;
  incremental: number | null;
  moderate: number | null;
  accelerated: number | null;
  transformational: number | null;
  scenario_position: string;
  attainment_pct: number | null;
  data_status: string | null;
  source_agency?: string | null;
  observation_date?: string | null;
  release_date?: string | null;
  source_url?: string | null;
  source_note?: string | null;
}

export interface InteractionEffect {
  name: string;
  interaction_type: string;
  max_effect_pp: number;
  intensity: number;
  contribution_pp: number;
}

export interface LagProfile {
  family: string;
  typical_lag: number | null;
  y1: number;
  y2: number;
  y3: number;
  y4: number;
  y5: number;
  basis_note: string | null;
}

export interface EvidenceItem {
  evidence_id: string;
  linkage_name: string;
  parameter_type: string | null;
  central_estimate: number | null;
  units: string | null;
  geography: string | null;
  reference: string | null;
  confidence: string | null;
  status: string | null;
}

export interface DataGap {
  variable_id: string;
  indicator: string;
  gap_classification: string | null;
  responsible_org: string | null;
  priority: string | null;
}

export interface AnalystAssumption {
  id: number;
  timestamp: string;
  variable_id: string;
  field_changed: string;
  old_value: string | null;
  new_value: string | null;
  analyst_note: string | null;
}

export interface SessionState {
  selected_scenario: string;
  custom_overrides: Record<string, number>;
  custom_interaction_intensity: number;
}

export interface ScenarioSnapshot {
  id: string;
  name: string;
  created_at: string;
  selected_scenario: string;
  custom_overrides: Record<string, number>;
  custom_interaction_intensity: number;
  note: string | null;
}

export interface ImportResult {
  imported: number;
  skipped: number;
  errors: string[];
}

export interface ObservationsSummary {
  total_observations: number;
  distinct_variables: number;
  last_imported_at: string | null;
}

export interface PolicyArea {
  id: string;
  name: string;
  color: string;
  sort_order: number;
}

export const SCENARIOS = [
  'Incremental Reform',
  'Moderate Reform',
  'Accelerated Reform',
  'Transformational Reform',
  'Custom Case',
] as const;

export const AREA_ROUTES: Record<string, string> = {
  exports: 'Exports',
  it_exports: 'IT Exports',
  macro_fiscal_stability: 'Macro & Fiscal Stability',
  industry: 'Industry',
  governance: 'Governance',
  bedrock_enablers: 'Bedrock Enablers',
  further_policy_levers: 'Further Policy Levers',
};
